/**
 * Muhurta Astrological Rationale Engine
 * 
 * Generates deeply contextual, classically authentic explanations for why a specific
 * muhurta window is suited for an exact activity or milestone.
 * 
 * Replaces generic boilerplate templates with multi-layered domain reasoning:
 * 1. Nakshatra Classification (Dhruva, Chara, Laghu, Mridu, etc.) matched to Milestone dynamics
 * 2. Planetary Lordship & Hora (Mercury for IP/domains, Venus for arts/marriage, Mars for surgery/property, etc.)
 * 3. Rashi Karakatvas & Drik moon placement
 * 4. Tara Bala personal energetic resonance (Sampat, Sadhaka, Kshema, Mitra, etc.)
 * 5. Panchanga factor synergy (Tithi category, Yoga meaning, Karana actionability)
 */

import { NakshatraTypeInfo } from './muhurtaEngine';
import { ZodiacSign } from '../types/kundali';

export interface MilestoneContext {
  id?: string;
  label?: string;
  category?: string;
  subCategory?: string;
}

export interface PanchangaSnapshot {
  nakshatra: string;
  nakshatraPada?: number;
  nakshatraType?: NakshatraTypeInfo;
  moonRashi: ZodiacSign | string;
  tithi: string;
  tithiNumber?: number;
  yoga: string;
  karana: string;
  hora: string;
}

export interface TaraBalaSnapshot {
  taraName?: string;
  suitability?: string;
  meaning?: string;
}

export interface GeneratedRationale {
  domainResonance: string;
  planetaryResonance: string;
  taraResonance?: string;
  panchangaHarmony: string;
  safetyShield: string;
}

/**
 * Maps categories or specific milestones to their semantic domain type
 */
function getDomainSemanticType(milestoneId?: string, categoryId?: string, label?: string): string {
  const normId = (milestoneId || '').toLowerCase();
  const normCat = (categoryId || '').toLowerCase();
  const normLabel = (label || '').toLowerCase();

  if (normId.includes('domain') || normId.includes('server') || normId.includes('app') || normId.includes('tech') || normCat.includes('tech') || normLabel.includes('domain')) {
    return 'tech_domain';
  }
  if (normId.includes('griha') || normId.includes('house') || normId.includes('bhoomi') || normId.includes('property') || normCat.includes('property') || normLabel.includes('house') || normLabel.includes('pravesh')) {
    return 'property_living';
  }
  if (normId.includes('vivaha') || normId.includes('marriage') || normId.includes('roka') || normId.includes('engagement') || normCat.includes('samskara') || normLabel.includes('marriage')) {
    return 'marriage_union';
  }
  if (normId.includes('vehicle') || normId.includes('car') || normId.includes('asset') || normCat.includes('assets') || normLabel.includes('vehicle')) {
    return 'vehicle_movable';
  }
  if (normId.includes('surgery') || normId.includes('medical') || normCat.includes('health') || normLabel.includes('surgery')) {
    return 'medical_surgery';
  }
  if (normId.includes('business') || normId.includes('shop') || normId.includes('office') || normId.includes('trade') || normCat.includes('business') || normLabel.includes('business')) {
    return 'business_commerce';
  }
  if (normId.includes('job') || normId.includes('career') || normId.includes('interview') || normCat.includes('career') || normLabel.includes('job')) {
    return 'career_vocation';
  }
  if (normId.includes('gemstone') || normId.includes('ruby') || normId.includes('sapphire') || normId.includes('emerald') || normCat.includes('gemstone')) {
    return 'gemstone_remedy';
  }
  if (normId.includes('invest') || normId.includes('stock') || normId.includes('finance') || normCat.includes('financial')) {
    return 'financial_growth';
  }
  if (normId.includes('travel') || normId.includes('abroad') || normId.includes('journey') || normCat.includes('travel')) {
    return 'travel_transit';
  }
  if (normId.includes('education') || normId.includes('study') || normId.includes('exam') || normCat.includes('education')) {
    return 'education_learning';
  }

  return 'general_auspicious';
}

/**
 * Generates Nakshatra classification resonance tailored to milestone domain
 */
export function generateNakshatraDomainRationale(
  nakshatra: string,
  nakshatraType: NakshatraTypeInfo | undefined,
  semanticType: string,
  activityName: string,
  isHi: boolean = false
): string {
  const categoryRaw = nakshatraType?.category || 'Dhruva';
  const isDhruva = categoryRaw.includes('Dhruva') || categoryRaw.includes('Fixed');
  const isChara = categoryRaw.includes('Chara') || categoryRaw.includes('Movable');
  const isLaghu = categoryRaw.includes('Laghu') || categoryRaw.includes('Light') || categoryRaw.includes('Swift');
  const isMridu = categoryRaw.includes('Mridu') || categoryRaw.includes('Soft');
  const isTikshna = categoryRaw.includes('Tikshna') || categoryRaw.includes('Sharp');
  const isUgra = categoryRaw.includes('Ugra') || categoryRaw.includes('Fierce');

  if (isHi) {
    if (semanticType === 'tech_domain') {
      if (isDhruva) {
        return `${nakshatra} एक 'ध्रुव/स्थिर' नक्षत्र है। शास्त्रीय मुहूर्त ग्रंथों के अनुसार डिजिटल ब्रांड, वेबसाइट एवं डोमेन पंजीकरण में स्थिर नक्षत्र का चयन नाम की स्थायित्व, ब्रांड पहचान की दीर्घायु और बार-बार बदलाव से सुरक्षा प्रदान करता है।`;
      }
      if (isChara) {
        return `${nakshatra} 'चर' संज्ञक नक्षत्र है। डिजिटल प्रणालियों में यह व्यापक ट्रैफिक प्रसार, नेटवर्क विस्तार और वैश्विक पहुंच के लिए अत्यंत अनुकूल वातावरण निर्मित करता है।`;
      }
      if (isLaghu) {
        return `${nakshatra} 'लघु/क्षिप्र' संज्ञक नक्षत्र है, जो त्वरित तकनीकी क्रियान्वयन, सहज DNS संचालन एवं तीव्र वाणिज्यिक शुभारंभ में विशेष फलदायी है।`;
      }
      if (isMridu) {
        return `${nakshatra} 'मृदु' संज्ञक नक्षत्र है, जो ब्रांड की सौम्य व आकर्षक छवि, रचनात्मक UI एवं उपयोगकर्ता विश्वास सुदृढ़ करने में सहायक है।`;
      }
      return `${nakshatra} नक्षत्र के प्रभाव में ${activityName} का शुभारंभ तकनीकी दक्षता एवं निरंतर प्रगति को बल प्रदान करता है।`;
    }

    if (semanticType === 'property_living') {
      if (isDhruva) {
        return `${nakshatra} 'ध्रुव/स्थिर' नक्षत्र होने के कारण गृह प्रवेश व नींव स्थापना के लिए सर्वोत्तम है। यह परिवार के स्थायी सुख, वास्तु स्थिरता एवं सुख-समृद्धि का आधार बनता है।`;
      }
      if (isMridu) {
        return `${nakshatra} 'मृदु' नक्षत्र है, जो नए आवास में शांति, पारिवारिक सौहार्द एवं मांगलिक वातावरण का संचार करता है।`;
      }
      return `${nakshatra} नक्षत्र संपत्ति एवं भवन से जुड़े कार्यों को अनुकूल वास्तु ऊर्जा प्रदान करता है।`;
    }

    if (semanticType === 'marriage_union') {
      return `${nakshatra} नक्षत्र दांपत्य में पारस्परिक प्रेम, दीर्घायु एवं कुल-वृद्धि के लिए अत्यंत शुभ व प्रशस्त माना गया है।`;
    }

    if (semanticType === 'vehicle_movable') {
      return `${nakshatra} ${isChara ? "एक 'चर' नक्षत्र होने से वाहन क्रय व यात्रा" : 'नक्षत्र वाहन क्रय'} के लिए मार्ग-सुरक्षा, यांत्रिक विश्वसनीयता एवं निर्बाध गतिशीलता सुनिश्चित करता है।`;
    }

    if (semanticType === 'business_commerce') {
      return `${nakshatra} ${isDhruva ? "'स्थिर' नक्षत्र होने से व्यावसायिक प्रतिष्ठान को स्थायी साख" : 'नक्षत्र व्यापार'} तथा ग्राहकों का निरंतर विश्वास प्रदान करता है।`;
    }

    if (semanticType === 'medical_surgery') {
      return `${nakshatra} नक्षत्र शल्यक्रिया में चिकित्सीय एकाग्रता, अल्पतम रक्तस्त्राव एवं त्वरित स्वास्थ्य-लाभ के लिए अनुकूल ऊर्जा देता है।`;
    }

    return `${nakshatra} (${nakshatraType?.sanskritType || 'शुभ नक्षत्र'}) के अंतर्गत ${activityName} का प्रारंभ शास्त्रीय दृष्टि से अभीष्ट सिद्धिदायक है।`;
  }

  // English
  if (semanticType === 'tech_domain') {
    if (isDhruva) {
      return `Governed by ${nakshatra}, a Dhruva (Fixed/Sthira) asterism. Classical Muhurta texts (Muhurta Chintamani, Brihat Samhita) mandate Sthira nakshatras for permanent titles, brand names, and digital asset reservations—anchoring ${activityName} with long-term domain authority and insulating against frequent rebranding or ownership churn.`;
    }
    if (isChara) {
      return `Transiting ${nakshatra}, a Chara (Movable) nakshatra. In modern digital governance, Chara energy catalyzes rapid network propagation, organic viral reach, and frictionless cross-border distribution for ${activityName}.`;
    }
    if (isLaghu) {
      return `Set in ${nakshatra}, a Laghu/Kshipra (Swift) asterism celebrated for rapid execution, agile deployment, and immediate operational momentum without bureaucratic or technical latency.`;
    }
    if (isMridu) {
      return `Positioned in ${nakshatra}, a Mridu (Tender/Refined) nakshatra. This infuses your digital presence with aesthetic elegance, high trust, and pleasant user resonance from inception.`;
    }
    return `Initiating under ${nakshatra} provides classical planetary alignment suited for modern technical undertakings, fostering steady growth and structural stability.`;
  }

  if (semanticType === 'property_living') {
    if (isDhruva) {
      return `Anchored in ${nakshatra} (Dhruva/Fixed). Classical Vastu-Muhurta texts prioritize fixed asterisms for residential foundation and entry, conferring domestic peace, rooted stability, and longevity to the household.`;
    }
    if (isMridu) {
      return `Illuminated by ${nakshatra} (Mridu/Gentle), infusing the new living space with domestic harmony, warmth, and auspicious family blessings.`;
    }
    return `The positive vibrations of ${nakshatra} provide grounding structural harmony for real estate and living spaces.`;
  }

  if (semanticType === 'marriage_union') {
    return `Blessed by ${nakshatra}, an auspicious asterism renowned for marital fidelity, shared spiritual harmony, emotional endurance, and familial prosperity.`;
  }

  if (semanticType === 'vehicle_movable') {
    return `${nakshatra} ${isChara ? '(a Chara/Movable nakshatra ideal for mechanical transit)' : 'provides favorable energetic momentum'}, conferring travel safety, mechanical longevity, and smooth journeys.`;
  }

  if (semanticType === 'business_commerce') {
    return `Set in ${nakshatra}, which ${isDhruva ? 'anchors commercial goodwill and client retention with fixed stability' : 'catalyzes healthy enterprise cash flows and trading expansion'}.`;
  }

  if (semanticType === 'medical_surgery') {
    return `Operated under ${nakshatra}, supporting physician focus, minimizing post-operative complications, and expediting cellular recuperation.`;
  }

  if (semanticType === 'career_vocation') {
    return `Supported by ${nakshatra}, providing executive clarity, professional prestige, and constructive recognition from leadership.`;
  }

  return `Initiating ${activityName} during this ${nakshatra} (${nakshatraType?.category.split(' ')[0] || 'Vedic'}) window activates classical alignment, ensuring the endeavor is supported by favorable celestial rhythms.`;
}

/**
 * Generates planetary & Drik Rashi + Hora resonance tailored to milestone domain
 */
export function generatePlanetaryDomainRationale(
  moonRashi: string,
  hora: string,
  semanticType: string,
  activityName: string,
  isHi: boolean = false
): string {
  const normRashi = moonRashi.toLowerCase();
  const normHora = hora.toLowerCase();

  // Planetary Lord of Rashi
  let rashiLord = 'Benefic Lord';
  if (normRashi.includes('taurus') || normRashi.includes('libra') || normRashi.includes('वृषभ') || normRashi.includes('तुला')) {
    rashiLord = 'Venus (Shukra)';
  } else if (normRashi.includes('gemini') || normRashi.includes('virgo') || normRashi.includes('मिथुन') || normRashi.includes('कन्या')) {
    rashiLord = 'Mercury (Budha)';
  } else if (normRashi.includes('cancer') || normRashi.includes('कर्क')) {
    rashiLord = 'Moon (Chandra)';
  } else if (normRashi.includes('leo') || normRashi.includes('सिंह')) {
    rashiLord = 'Sun (Surya)';
  } else if (normRashi.includes('aries') || normRashi.includes('scorpio') || normRashi.includes('मेष') || normRashi.includes('वृश्चिक')) {
    rashiLord = 'Mars (Mangala)';
  } else if (normRashi.includes('sagittarius') || normRashi.includes('pisces') || normRashi.includes('धनु') || normRashi.includes('मीन')) {
    rashiLord = 'Jupiter (Guru)';
  } else if (normRashi.includes('capricorn') || normRashi.includes('aquarius') || normRashi.includes('मकर') || normRashi.includes('कुंभ')) {
    rashiLord = 'Saturn (Shani)';
  }

  const isMoonExalted = normRashi.includes('taurus') || normRashi.includes('वृषभ');
  const isMercuryConnected = normHora.includes('mercury') || normHora.includes('बुध') || rashiLord.includes('Mercury');
  const isVenusConnected = normHora.includes('venus') || normHora.includes('शुक्र') || rashiLord.includes('Venus');
  const isJupiterConnected = normHora.includes('jupiter') || normHora.includes('गुरु') || rashiLord.includes('Jupiter');

  if (isHi) {
    if (semanticType === 'tech_domain') {
      if (isMoonExalted && isMercuryConnected) {
        return `चंद्रमा वृषभ राशि में उच्चस्थ हैं (शुक्र शासित, जो ब्रांड सौन्दर्य व वाणिज्यिक मूल्य के कारक हैं) तथा समय पर ${hora} होरा सक्रिय है। वैदिक ज्योतिष में बुध संचार, बौद्धिक संपदा, डोमेन/कागजात व अनुबंध के कारक हैं। यह युति डोमेन को तकनीकी स्थिरता एवं व्यावसायिक आकर्षण प्रदान करती है।`;
      }
      if (isMercuryConnected) {
        return `चंद्रमा का ${moonRashi} में गोचर एवं सक्रिय ${hora} होरा संचार तंत्र, कोड निष्पादन तथा डिजिटल समझौतों के लिए अत्यंत सहायक है, जिससे तकनीकी त्रुटियों की संभावना क्षीण होती है।`;
      }
      return `${moonRashi} में चंद्रमा का गोचर एवं ${hora} होरा की सक्रियता इस डिजिटल प्रक्रिया को संतुलित स्थिरता व सुचारू परिचालन प्रदान करती है।`;
    }

    if (semanticType === 'property_living') {
      return `${moonRashi} में स्थित चंद्रमा एवं सक्रिय ${hora} होरा भवन के वास्तु तत्वों को संतुलित कर नए परिसर में शुभ ऊर्जा, वित्तीय समृद्धि व आंतरिक शांति स्थापित करते हैं।`;
    }

    if (semanticType === 'business_commerce') {
      return `${moonRashi} में चंद्रमा का गोचर एवं ${hora} होरा व्यापारिक वार्ता, धन प्रवाह व ग्राहकों के साथ सौहार्दपूर्ण संबंध बनाए रखने में समर्थ हैं।`;
    }

    return `चंद्रमा का ${moonRashi} में संचरण एवं सक्रिय ${hora} होरा इस समय-विंडो को व्यावहारिक स्थिरता व ऊर्जावान गति प्रदान करते हैं।`;
  }

  // English
  if (semanticType === 'tech_domain') {
    if (isMoonExalted && isMercuryConnected) {
      return `The Moon is exalted in Taurus (governed by Venus, Karaka of commercial aesthetics and valuation), synchronized with an active ${hora} Hora. In classical Jyotish, Mercury (Budha) is the celestial significator of digital communication, intellectual property protocols, contracts, and nomenclature. This pairing grants your domain registration authoritative brand equity coupled with seamless technical propagation.`;
    }
    if (isMercuryConnected) {
      return `Moon in ${moonRashi} coupled with the active ${hora} Hora directly stimulates the natural significations of Mercury (Budha)—governor of data networks, URLs, intellectual property rights, and commercial agreements—minimizing registrar or administrative friction.`;
    }
    if (isVenusConnected) {
      return `Moon in ${moonRashi} under a ${hora} Hora channels Venusian grace, favoring creative brand appeal, memorable digital naming, and strong aesthetic resonance with end-users.`;
    }
    if (isJupiterConnected) {
      return `Moon in ${moonRashi} under an auspicious ${hora} Hora invokes Guru's benevolent expansion, sanctifying intellectual enterprise and moral legitimacy.`;
    }
    return `The transit of the Moon in ${moonRashi} combined with the active ${hora} Hora channels planetary stability, allowing digital assets and systems to function with minimal operational friction.`;
  }

  if (semanticType === 'property_living') {
    return `The Moon’s placement in ${moonRashi} alongside the active ${hora} Hora anchors the spatial Vastu geometry, inviting enduring prosperity, domestic equanimity, and physical security into the premises.`;
  }

  if (semanticType === 'marriage_union') {
    return `With the Moon gracefully positioned in ${moonRashi} during a harmonious ${hora} Hora, the window promotes emotional receptivity, mutual respect, and long-term marital concord.`;
  }

  if (semanticType === 'business_commerce') {
    return `Moon transiting ${moonRashi} under an active ${hora} Hora stimulates transactional liquidity, professional credibility, and constructive client engagement.`;
  }

  if (semanticType === 'medical_surgery') {
    return `The Moon’s calm passage through ${moonRashi} under the protective shield of ${hora} Hora assists the surgical team with steady nerves and optimal physiological endurance.`;
  }

  return `The transit of the Moon in ${moonRashi} alongside the active ${hora} Hora provides vital planetary balance, ensuring all associated assets and undertakings operate with sustained poise.`;
}

/**
 * Generates personalized Tara Bala resonance
 */
export function generateTaraDomainRationale(
  taraName: string,
  suitability: string,
  semanticType: string,
  isHi: boolean = false
): string {
  const normTara = (taraName || '').toLowerCase();

  if (isHi) {
    if (normTara.includes('sampat')) {
      return `जातक के लिए यह 'सम्पत् तारा' (२री तारा) है, जो धन, समृद्धि एवं व्यावसायिक परिसंपत्ति अर्जन का प्रतीक है। यह निवेशित पूंजी की रक्षा एवं निरंतर लाभ का मार्ग प्रशस्त करती है।`;
    }
    if (normTara.includes('sadhaka')) {
      return `यह जातक के लिए 'साधक तारा' (६ठी तारा) है, जो कार्य-सिद्धि, तकनीकी दक्षता एवं लक्ष्यों की निर्विघ्न प्राप्ति का वरदान देती है।`;
    }
    if (normTara.includes('kshema')) {
      return `यह 'क्षेम तारा' (४थी तारा) है, जो सुरक्षा, कल्याण, अनपेक्षित विघ्नों से रक्षा एवं स्थिरता का शास्त्रीय कवच प्रदान करती है।`;
    }
    if (normTara.includes('param') || normTara.includes('mitra')) {
      return `यह 'मित्र/परम मित्र तारा' है, जो सहयोगी संबंधों, साझेदारों के सहयोग एवं जनसाधारण से अनुकूल व्यवहार प्राप्त कराती है।`;
    }
    return `जातक की जन्म कुंडली के अनुसार यह '${taraName} तारा' (${suitability}) में स्थित है, जो व्यक्तिगत रूप से अनुकूल व फलदायी परिणाम सुनिश्चित करती है।`;
  }

  // English
  if (normTara.includes('sampat')) {
    return `Blessed with Sampat Tara (the 2nd Tara of wealth and resource acquisition). When securing an intellectual or commercial asset, Sampat Tara shields invested capital, prevents resource drain, and channels lucrative long-term monetization.`;
  }
  if (normTara.includes('sadhaka')) {
    return `Blessed with Sadhaka Tara (the 6th Tara of accomplishment and technical mastery). It specifically grants focus, goal fulfillment, and clean milestone execution without mid-way stalling.`;
  }
  if (normTara.includes('kshema')) {
    return `Blessed with Kshema Tara (the 4th Tara of security and well-being). It forms a protective energetic buffer, shielding your undertaking from unexpected disputes, cancellations, or volatile reversals.`;
  }
  if (normTara.includes('param') || normTara.includes('mitra')) {
    return `Blessed with ${taraName} Tara (${suitability}). It fosters harmonious stakeholder alignment, favorable counterparty negotiation, and goodwill from patrons or customers.`;
  }

  return `Blessed with ${taraName} Tara (${suitability}), ensuring the native receives direct auspicious merit, protection from hurdles, and productive fruition.`;
}

/**
 * Generates Panchanga Harmony rationale for Tithi & Yoga
 */
export function generatePanchangaHarmonyRationale(
  tithi: string,
  yoga: string,
  karana: string,
  isHi: boolean = false
): string {
  const normY = (yoga || '').toLowerCase();
  const normK = (karana || '').toLowerCase();
  const isInauspiciousYoga = normY.includes('vyatipata') || normY.includes('vaidhriti') || normY.includes('atiganda') || normY.includes('shula') || normY.includes('ganda');
  const isVishti = normK.includes('vishti') || normK.includes('bhadra');

  if (isHi) {
    if (isVishti || isInauspiciousYoga) {
      return `तिथि '${tithi}', योग '${yoga}' एवं करण '${karana}' का शास्त्रीय परीक्षण किया गया है। विशेष सावधानियों एवं परिहार नियमों का अनुपालन आवश्यक है।`;
    }
    return `तिथि '${tithi}' एवं शुभ योग '${yoga}' का समन्वय इस काल-खंड को मांगलिक ऊर्जा से पुष्ट करता है। करण '${karana}' कर्म में गति एवं सफलता का संचार करता है।`;
  }

  if (isVishti || isInauspiciousYoga) {
    return `Evaluated across ${tithi}, ${yoga} Yoga, and ${karana} Karana. Classical restrictions or cautionary Parihara rules apply to this temporal alignment.`;
  }

  return `Supported by harmonious ${tithi} and auspicious ${yoga} Yoga, alongside actionable ${karana} Karana—a classical alignment that fosters steady progress and protects the endeavor from unexpected retrograde setbacks.`;
}

/**
 * Primary Facade: Generates full contextual rationale object
 */
export function generateMuhurtaRationale(
  milestone: MilestoneContext,
  panchang: PanchangaSnapshot,
  taraBala?: TaraBalaSnapshot,
  isHi: boolean = false
): GeneratedRationale {
  const semanticType = getDomainSemanticType(milestone.id, milestone.category, milestone.label);
  const activityLabel = milestone.label || 'the undertaking';

  const domainResonance = generateNakshatraDomainRationale(
    panchang.nakshatra,
    panchang.nakshatraType,
    semanticType,
    activityLabel,
    isHi
  );

  const planetaryResonance = generatePlanetaryDomainRationale(
    panchang.moonRashi,
    panchang.hora,
    semanticType,
    activityLabel,
    isHi
  );

  const taraResonance = taraBala?.taraName
    ? generateTaraDomainRationale(taraBala.taraName, taraBala.suitability || 'Auspicious', semanticType, isHi)
    : undefined;

  const panchangaHarmony = generatePanchangaHarmonyRationale(
    panchang.tithi,
    panchang.yoga,
    panchang.karana,
    isHi
  );

  const safetyShield = isHi
    ? 'राहु काल एवं यमघण्ड जैसे अशुभ अंतरालों से सर्वथा मुक्त, जिससे कार्य का आरंभ बिना किसी आकस्मिक अवरोध के सुगमता से संपन्न होता है।'
    : 'Strictly filtered with zero overlap across Rahu Kalam, Yamaganda, or Durmuhurta, ensuring initiation proceeds smoothly without sudden obstruction or latency.';

  return {
    domainResonance,
    planetaryResonance,
    taraResonance,
    panchangaHarmony,
    safetyShield
  };
}
